/**
 * Search — step drill-in: the matched / dropped / no-answer groups behind one
 * funnel step, with per-lead provenance chips (which fact matched, from where,
 * verified or not) so a result can be TRUSTED or challenged.
 *
 * Two modes:
 *  - preview: renders the builder's 5-per-group samples (fast peek);
 *  - run: pages the full groups from GET /search-runs/:id/steps/:stepId
 *    (recomputed from the frozen definition against current data).
 *
 * The Dropped count comes from searchLib.droppedWithData — the same function the
 * step card uses, so the two never disagree. Opens on whichever group was
 * clicked (`initialBucket`).
 */
import { useCallback, useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import api from '../../../services/api';
import Modal from '../../../ui/Modal';
import { SegmentedControl } from '../filters';
import { LifecycleBadge } from '..';
import type { SearchLeadRow, SearchPreviewStep, SearchStepDef, SearchStepStats } from '../../types';
import { prettySlug, relTime } from '../../lib';
import { describeWarning, droppedWithData, isQuestionStep, unknownWords } from '../../pages/search/searchLib';
import { Alert } from '../ui';

/** The three groups behind a step (the server's bucket names). */
export type DrillBucket = 'matched' | 'excluded' | 'no_data';

interface Props {
  open: boolean;
  onClose: () => void;
  stepStats: SearchStepStats | null;
  stepTitle: string;
  mode: 'preview' | 'run';
  /** preview mode: the builder's per-bucket samples. */
  samples?: SearchPreviewStep['samples'];
  /** run mode: the run to page buckets from. */
  runId?: string;
  onOpenLead?: (id: string) => void;
  /** The group to open on (a step card's "N dropped" / "N no answer" click). Default: matched. */
  initialBucket?: DrillBucket;
  /** The step's definition, when the caller has it: exact "dropped" arithmetic, question wording,
   *  and whether keeping the no-answer people is sound advice (not when the step is form-pinned). */
  step?: SearchStepDef | null;
}

const SOURCE_LABELS: Record<string, string> = {
  form: 'Form', ai_extraction: 'AI', recruiter: 'Recruiter', lead: 'Landing page', prescreen: 'Prescreen',
};

const errText = (e: any): string =>
  e?.response?.data?.error || "Couldn't load these people. Try again in a moment.";

function FactChips({ row }: { row: SearchLeadRow }) {
  const facts = row.matched_facts || [];
  if (!facts.length) return null;
  return (
    <div className="mt-0.5 flex flex-wrap gap-1">
      {facts.map((f, i) => (
        <span
          key={i}
          title={f.excerpt ? `"${f.excerpt}"` : undefined}
          className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-[11px] ${
            f.verified
              ? 'border-green-300 bg-green-50 text-green-800 dark:border-green-800 dark:bg-green-900/20 dark:text-green-300'
              : 'border-gray-200 bg-gray-50 text-gray-600 dark:border-gray-600 dark:bg-gray-700/60 dark:text-gray-300'
          }`}
        >
          <span className="font-medium">{f.label}</span>
          {f.detail && <span className="text-gray-400">{f.detail}</span>}
          <span className="text-gray-400">·</span>
          <span>{SOURCE_LABELS[f.source || ''] || (f.source ? prettySlug(f.source) : 'Unknown source')}{f.confidence ? ` (${f.confidence})` : ''}</span>
          {f.verified && <span>✓</span>}
        </span>
      ))}
    </div>
  );
}

function LeadRow({ row, onOpen }: { row: SearchLeadRow; onOpen?: (id: string) => void }) {
  return (
    <li>
      <button
        type="button"
        onClick={onOpen ? () => onOpen(row.id) : undefined}
        className="block w-full rounded-lg px-2 py-1.5 text-left transition-colors duration-150 hover:bg-gray-50 dark:hover:bg-gray-700/40"
      >
        <div className="flex items-center gap-2">
          <span className="min-w-0 flex-1 truncate text-sm font-medium text-gray-800 dark:text-gray-100">
            {row.full_name || `${row.first_name || ''} ${row.last_name || ''}`.trim() || row.phone_e164 || row.email || 'Unnamed lead'}
          </span>
          <LifecycleBadge status={row.lifecycle_status} />
          {row.effective_study_slug && (
            <span className="hidden text-xs text-gray-400 sm:inline">{prettySlug(row.effective_study_slug)}</span>
          )}
          {row.created_at && <span className="hidden text-xs text-gray-400 md:inline">{relTime(row.created_at)}</span>}
        </div>
        <FactChips row={row} />
      </button>
    </li>
  );
}

export default function StepDrillIn({
  open, onClose, stepStats, stepTitle, mode, samples, runId, onOpenLead, initialBucket = 'matched', step,
}: Props) {
  const [bucket, setBucket] = useState<DrillBucket>(initialBucket);
  const [rows, setRows] = useState<SearchLeadRow[]>([]);
  const [total, setTotal] = useState<number | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchPage = useCallback(async (b: DrillBucket, offset: number, replace: boolean) => {
    if (mode !== 'run' || !runId || !stepStats) return;
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/api/crm/search-runs/${runId}/steps/${stepStats.step_id}`, {
        params: { bucket: b, offset, limit: 25 },
      });
      const batch: SearchLeadRow[] = res.data?.rows || [];
      setRows((prev) => (replace ? batch : [...prev, ...batch]));
      setTotal(res.data?.total ?? null);
    } catch (e: any) {
      setError(errText(e));
    } finally {
      setLoading(false);
    }
  }, [mode, runId, stepStats]);

  useEffect(() => {
    if (!open) return;
    setBucket(initialBucket);
    setRows([]);
    setTotal(null);
    if (mode === 'run') fetchPage(initialBucket, 0, true);
  }, [open, mode, fetchPage, initialBucket]);

  const switchBucket = (b: DrillBucket) => {
    setBucket(b);
    if (mode === 'run') {
      setRows([]);
      fetchPage(b, 0, true);
    }
  };

  if (!open || !stepStats) return null;
  const question = isQuestionStep(step);
  const words = unknownWords(question);
  const pinned = Boolean(step?.conditions.some((c) => c.form_id));
  const previewRows = mode === 'preview' ? (samples?.[bucket] || []) : rows;
  const bucketCounts: Record<DrillBucket, number> = {
    matched: stepStats.matched,
    excluded: droppedWithData(stepStats, step),
    no_data: stepStats.no_data,
  };

  return (
    <Modal isOpen={open} onClose={onClose} title={stepTitle || 'Who this step kept and dropped'} maxWidth="max-w-2xl">
      <div className="space-y-3">
        <div className="flex flex-wrap items-center gap-2">
          <SegmentedControl
            label="Group"
            value={bucket}
            onChange={(v) => switchBucket(v as DrillBucket)}
            options={[
              { value: 'matched', label: `Matched (${bucketCounts.matched.toLocaleString()})` },
              { value: 'excluded', label: `Dropped (${bucketCounts.excluded.toLocaleString()})` },
              { value: 'no_data', label: `${words.tab} (${bucketCounts.no_data.toLocaleString()})` },
            ]}
          />
          {stepStats.coverage_pct < 100 && (
            <span className="text-xs text-gray-500 dark:text-gray-400">
              {stepStats.coverage_pct}% of the people reaching this step {words.have}
            </span>
          )}
        </div>

        {stepStats.warnings.length > 0 && (
          <Alert tone="warning" size="sm" icon={false}>
            <ul className="space-y-0.5">
              {stepStats.warnings.map((w) => (
                <li key={w}>{describeWarning(w, { coveragePct: stepStats.coverage_pct, question })}</li>
              ))}
            </ul>
          </Alert>
        )}
        {error && (
          <Alert tone="error" size="sm" icon={false}>{error}</Alert>
        )}

        {bucket === 'no_data' && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {question
              ? 'These people have no answer on file for this question — the step could neither match them nor rule them out.'
              : 'These people have nothing on file for this — the step could neither match them nor rule them out.'}
            {/* Keeping them is sound only when the step isn't pinned to one form: with a pin, "no
                answer" includes everyone who never saw that form. */}
            {step && !pinned && ` To keep them anyway, set "${words.people}" to "keep them" on the step.`}
          </p>
        )}
        {bucket === 'excluded' && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            These people {question ? 'answered' : 'have this on file'}, but the step ruled them out.
          </p>
        )}

        <ul className="max-h-[50vh] space-y-0.5 overflow-y-auto">
          {previewRows.map((r) => <LeadRow key={r.id} row={r} onOpen={onOpenLead} />)}
          {previewRows.length === 0 && !loading && (
            <li className="px-2 py-4 text-sm text-gray-400">Nobody here.</li>
          )}
        </ul>

        {loading && <Loader2 className="mx-auto h-5 w-5 animate-spin text-gray-400" />}
        {mode === 'preview' ? (
          <p className="text-xs text-gray-400">
            Showing up to 5 people per group — run the search to see everyone.
          </p>
        ) : (
          total !== null && rows.length < total && !loading && (
            <button
              type="button"
              onClick={() => fetchPage(bucket, rows.length, false)}
              className="w-full rounded-lg border border-gray-300 py-1.5 text-sm font-medium text-gray-600 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700"
            >
              Load more ({rows.length.toLocaleString()} of {total.toLocaleString()})
            </button>
          )
        )}
      </div>
    </Modal>
  );
}
